import React, { useEffect, useMemo, useState } from 'react';
import { OneWordRecord, normalizeWordKey } from '@/services/oneWordRecordsService';
import { OneWordRace, RaceRange, getOneWordRaces } from '@/services/oneWordRacesService';
import OneWordLetterErrorChart from './OneWordLetterErrorChart';

interface Props {
  uid?: string;
  word: string;
  race: { bestLapMs: number; bestStreak: number; lapsCompleted: number };
  record: OneWordRecord | null;
  onNewRound?: () => void;
}

const RANGES: RaceRange[] = ['day', 'week', 'month', 'all'];

const fmtMs = (ms: number): string => {
  if (!ms || !isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

/**
 * Signed percentage of `value` vs the personal average. `lowerBetter` flips the
 * sense of "good" (Best Lap: faster = better). Returns the raw signed delta plus
 * whether it represents an improvement (for coloring).
 */
const deltaVsAvg = (value: number, avg: number | undefined, lowerBetter: boolean) => {
  if (!avg || avg <= 0 || !value || value <= 0) return null;
  const raw = ((value - avg) / avg) * 100;
  const improvement = lowerBetter ? raw < 0 : raw > 0;
  return { text: `${raw >= 0 ? '+' : ''}${raw.toFixed(0)}%`, improvement };
};

const StatBox: React.FC<{
  label: string;
  value: string;
  delta: { text: string; improvement: boolean } | null;
}> = ({ label, value, delta }) => (
  <div className="owrc-stat">
    <div className="owrc-stat-label">{label}</div>
    <div className="owrc-stat-value">{value}</div>
    {delta ? (
      <div className={`owrc-stat-delta ${delta.improvement ? 'up' : 'down'}`}>
        {delta.text} <span className="owrc-stat-delta-note">vs avg</span>
      </div>
    ) : (
      <div className="owrc-stat-delta neutral">—</div>
    )}
  </div>
);

const OneWordRaceComplete: React.FC<Props> = ({ uid, word, race, record, onNewRound }) => {
  const [range, setRange] = useState<RaceRange>('all');
  const [races, setRaces] = useState<OneWordRace[]>([]);
  const wordKey = useMemo(() => normalizeWordKey(word), [word]);

  // Refetch when the aggregate record updates (lastTrainedAt) — this fires once the
  // just-finished race has been persisted, so the chart includes the latest race.
  useEffect(() => {
    if (!uid || !wordKey) return;
    let cancelled = false;
    getOneWordRaces(uid, { wordKey, range }).then((rows) => {
      if (!cancelled) setRaces(rows);
    });
    return () => { cancelled = true; };
  }, [uid, wordKey, range, record?.lastTrainedAt]);

  const lapDelta = deltaVsAvg(race.bestLapMs, record?.avgRaceBestLapMs, true);
  const flowDelta = deltaVsAvg(race.bestStreak, record?.avgRaceBestStreak, false);
  const raceDelta = deltaVsAvg(race.lapsCompleted, record?.avgRaceLaps, false);

  return (
    <div className="owrc">
      <div className="owrc-heading">Training Complete</div>
      <div className="owrc-word">"{word}"</div>

      <div className="owrc-stats">
        <StatBox label="Best Lap" value={fmtMs(race.bestLapMs)} delta={lapDelta} />
        <StatBox label="Best Flow" value={String(race.bestStreak)} delta={flowDelta} />
        <StatBox label="Best Race" value={String(race.lapsCompleted)} delta={raceDelta} />
      </div>

      <div className="owrc-chart-head">
        <span className="owrc-chart-title">Top mistaken letters</span>
        <div className="owrc-range">
          {RANGES.map((r) => (
            <button
              key={r}
              className={`owrc-range-btn ${range === r ? 'active' : ''}`}
              onClick={() => setRange(r)}
            >
              {r === 'all' ? 'All' : r[0].toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <OneWordLetterErrorChart races={races} topN={7} height={190} />

      {onNewRound && (
        <button className="owrc-new-round" onClick={onNewRound}>New Race</button>
      )}

      <style>{`
        .owrc {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px;
          background: rgba(17, 24, 39, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          backdrop-filter: blur(10px);
          width: 300px;
          box-sizing: border-box;
        }
        .owrc-heading {
          font-size: 1rem;
          font-weight: 700;
          color: #ffffff;
          text-align: center;
        }
        .owrc-word {
          font-size: 0.8rem;
          color: #cbd5e1;
          text-align: center;
          margin-top: -4px;
        }
        .owrc-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
        }
        .owrc-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 6px 2px;
          background: rgba(0, 0, 0, 0.25);
          border-radius: 8px;
        }
        .owrc-stat-label {
          font-size: 0.58rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .owrc-stat-value {
          font-size: 0.95rem;
          font-weight: 700;
          color: #f8fafc;
          font-variant-numeric: tabular-nums;
        }
        .owrc-stat-delta {
          font-size: 0.62rem;
          font-weight: 600;
          font-variant-numeric: tabular-nums;
        }
        .owrc-stat-delta-note { color: #64748b; font-weight: 500; }
        .owrc-stat-delta.up { color: #22c55e; }
        .owrc-stat-delta.down { color: #f87171; }
        .owrc-stat-delta.neutral { color: #64748b; }
        .owrc-chart-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .owrc-chart-title {
          font-size: 0.66rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .owrc-range {
          display: flex;
          gap: 2px;
        }
        .owrc-range-btn {
          padding: 2px 6px;
          font-size: 0.6rem;
          color: #94a3b8;
          background: rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 4px;
          cursor: pointer;
        }
        .owrc-range-btn.active {
          color: #ffffff;
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.5);
        }
        .owrc-new-round {
          margin-top: 2px;
          padding: 8px;
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
        }
        .owrc-new-round:hover { transform: translateY(-1px); }
      `}</style>
    </div>
  );
};

export default OneWordRaceComplete;
